import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlatList } from 'react-native-gesture-handler';
import { getSyncingStatus, setReconsentConnections, syncAllConnections } from 'actions';
import { AppView, Button, CardView, rem, withAnimatedNavigationHeader } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ListEmptyView from 'design-system/ListEmptyView';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import ToolTip from 'design-system/ToolTip';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectOpenConnectionsWithConsentExpiresAt } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { isWeb } from '../../../constants';
import ConnectionExpiryInfoCard from './components/ConnectionExpiryInfoCard';
import { sortedConnectionsByExpiresAt } from './utils';
const strings = {
    continueSharing: 'Keep sharing your data',
    inOrderToKeepSyncing: 'To keep your accounts in sync, UK regulations require you to confirm every 90 days that you’re happy for Emma to access your bank data. Just complete this quick check — no further action needed.',
    whyAreSomeNotOnTheList: 'Why are some of my banks not on the list?',
    whyAreSomeNotOnTheListMultiline: 'Why are some of my banks\nnot on the list?',
    thanksToNewRegulations: 'Thanks to new regulations, users don’t need to log back into their banking apps every 90 days. If your bank is missing, it means they are still in the process of migrating, so you will need to renew access from the Feed when the time is due. Emma will notify you.',
};
const keyExtractor = (connection) => connection.id.toString();
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);
const LoginsReconsentScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const isModal = route.params?.isModal;
    const connections = useAppSelector(selectOpenConnectionsWithConsentExpiresAt);
    const sortedByExpiresAt = useMemo(() => sortedConnectionsByExpiresAt(connections), [connections]);
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerLeft: isModal && !isWeb ? () => <NavigationCloseButton onPress={() => navigation.pop()}/> : undefined,
            headerBackVisible: !isModal,
        });
    }, [isModal]);
    const renderHeader = useCallback(() => <ScreenInfoHeader title={strings.continueSharing} subTitle={strings.inOrderToKeepSyncing}/>, []);
    const renderItem = useCallback(({ item }) => <ConnectionExpiryInfoCard connection={item}/>, []);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const isEmpty = !connections || connections.length === 0;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const onPressConfirm = useCallback(async () => {
        const ids = connections.map((connection) => connection.id);
        const res = await fetch(setReconsentConnections(ids));
        if (!res)
            return;
        const result = await fetch(syncAllConnections());
        if (result?.isSyncingManual) {
            dispatch(getSyncingStatus());
        }
        navigation.navigateDeprecated('Success', {
            subTitle: 'You’ve granted us consent to keep syncing to your banks. We’ll ask this every 90 days to follow the regulations.',
            onContinue: () => {
                navigation.pop(2);
                return true;
            },
            buttonText: 'Done',
        });
    }, [connections, dispatch]);
    const ListFooterComponent = useMemo(() => {
        if (isEmpty)
            return null;
        return (<View style={styles.footer}>
          <ToolTip centered title={strings.whyAreSomeNotOnTheListMultiline} message={strings.whyAreSomeNotOnTheList} subtitle={strings.thanksToNewRegulations}/>
        </View>);
    }, [isEmpty]);
    const ListEmptyComponent = useMemo(() => (<CardView withPadding noMarginTop>
          <ListEmptyView happy compact title="All good" subTitle="You have no more connections requiring consent."/>
        </CardView>), []);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <AnimatedFlatList onScroll={scrollHandler} data={sortedByExpiresAt} keyExtractor={keyExtractor} renderItem={renderItem} ListHeaderComponent={renderHeader} ListFooterComponent={ListFooterComponent} ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle}/>
        {!isEmpty && (<Button brand floating title="Confirm" onPress={onPressConfirm} isFetching={isFetching} preventDoubleTap/>)}
      </AppView>);
}, ({ colors }) => ({
    title: 'Reconsent',
    hasCustomTitle: true,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    footer: {
        paddingTop: rem(10),
        paddingHorizontal: rem(16),
    },
});
export default LoginsReconsentScreen;
